import { useContext } from 'react';
import * as React from 'react';
import merge from 'lodash.merge';
import { Locale, Mode } from './types';
import { extendI18n, changeLocale, captureErrorSentry } from './utils';
import { setApiUrl } from './graphql-client';
import { StepsEnum } from './types';
import { OauthServiceType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { Sorting } from './Results/enums';
import ConfigSchema from './configSchema/index';
export const defaultPassengersConfig = [
    {
        code: 'ADT',
        isAdult: true,
        defaultCount: 1
    },
    {
        code: 'CLD',
        isAdult: false,
        maxCountWithoutAdult: 0
    },
    {
        code: 'INF',
        isAdult: false
    },
    {
        code: 'INS',
        isAdult: false
    }
];
export const defaultConfig = {
    FlightStatus: {
        engineURL: '',
        appStoreURL: 'https://apps.apple.com/id/app/websky-app/id1567545689',
        playMarketURL: 'https://play.google.com/store/apps/details?id=aero.websky.demo',
        accessToken: 'pk.eyJ1IjoiZXZnZW55bGl0dmluZW5rb21sc2QiLCJhIjoiY2xrYzZoamRsMDM1ajNkcWp3aDF0cDlrYiJ9.Z0TikOCrmMUNhc7kYUT7TQ',
        stylesEn: 'mapbox://styles/evgenylitvinenkomlsd/clq2mj6h301tk01pa3xaqg95c',
        stylesRu: 'mapbox://styles/evgenylitvinenkomlsd/clkv3s6c8001z01pd51do54s1'
    },
    SearchForm: {
        redwingsForeignCardPaymentCheckbox: false,
        enableComplexRoute: true,
        mobileFormType: 'alternative',
        engineURL: '',
        payByMiles: false,
        requestCoordintates: {
            desktop: true,
            mobile: true
        },
        maxPassengersCount: 9,
        passengersConfig: defaultPassengersConfig,
        offerHotels: false,
        offerHotelsLink: 'https://ostrovok.ru/hotels'
    },
    Engine: {
        searchFormURL: '',
        checkInURL: '',
        accountURL: '',
        mealWarning: '',
        promoLoaders: [],
        checkInEnabled: false,
        showRefundRedirectButton: false,
        calendarDaysCount: 7,
        showAvailableFaresOnly: false,
        sortResultPreferCurrentAirline: false,
        resultsFareSelectorInModal: true,
        ignoreBlockedPaymentToReturnOrder: true,
        googleOneTapSignInClientId: null,
        exareShowPassengersStep: true,
        resultsDefaultSorting: Sorting.Price,
        nas: {
            suggestServicesTypes: [
                OrderAdditionalServiceGdsServiceServiceType.Baggage,
                OrderAdditionalServiceGdsServiceServiceType.Seat,
                OrderAdditionalServiceGdsServiceServiceType.Meal
            ],
            suggestedServicesOrder: {
                insurance: 0,
                insuranceGroups: 0,
                [OrderAdditionalServiceGdsServiceServiceType.Meal]: 1,
                [OrderAdditionalServiceGdsServiceServiceType.Baggage]: 2,
                [OrderAdditionalServiceGdsServiceServiceType.Seat]: 3
            }
        }
    },
    Account: {
        engineURL: '',
        showUserPhoneInput: true,
        showCommunications: false,
        showSubscribeCheckbox: false
    },
    Checkin: {
        CheckinConditions: ['OpenCloseCheckinCondition', 'BaggageCondition', 'PassengersCondition'],
        showConsents: false
    },
    global: {
        additionalResources: {
            scripts: ['https://secure.sirena-travel.ru/media/js/pg-additional-1.0.min.js'],
            extraScripts: [],
            svgs: ['https://cdn.websky.aero/content/ZZ/images/icons.svg'],
            extraSvgs: []
        },
        socialAuthMethods: [
            { service: OauthServiceType.Vkontakte, isAvailable: false },
            { service: OauthServiceType.Google, isAvailable: false },
            { service: OauthServiceType.Facebook, isAvailable: false },
            { service: OauthServiceType.TinkoffID, isAvailable: false }
        ],
        authMethods: {
            phone: false,
            ffp: true,
            email: true
        },
        showIsActualizationResultOk: true,
        allowPromoCodes: false,
        locale: Locale.Russian,
        i18n: {},
        findOrderURL: '',
        seatMap: {
            splitByCabin: false,
            showRecommendedSeats: false
        },
        insurance: {
            upsaleStrategy: ['checkout-order-service'],
            allowImplicitConfirm: true,
            multiplePriorityInsurances: false,
            insurancePriorities: []
        },
        reserveEngines: {
            siteUrl: 'https://websky.tech'
        },
        companyInfo: {
            loyaltyProgramName: 'Websky Club',
            iataCode: 'ZZ',
            exarePrivacyPolicyURL: 'https://google.com',
            exareRulesURL: 'https://google.com',
            subsidyFaresConditionURL: 'https://websky.aero/',
            transferM2ConditionURL: null,
            loyaltyProgramRegistrationUrl: '',
            loyaltyProgramAccountUrl: null,
            loyalty: {
                links: [
                    {
                        title: 'Statuses',
                        url: 'https://websky.tech'
                    },
                    {
                        title: 'Earn',
                        url: 'https://websky.tech'
                    },
                    {
                        title: 'Use',
                        url: 'https://websky.tech'
                    },
                    {
                        title: 'FAQ',
                        url: 'https://websky.tech'
                    }
                ]
            },
            multiInsurance: true,
            specialDocumentForInsurance: true
        },
        servicesAvailable: {
            animal: false
        },
        stepsAvailable: {
            [StepsEnum.Meal]: true
        },
        FFPRegex: /^\d{6}$/,
        roundMoneyAmount: false,
        purchasePredictionThreshold: 0.6,
        showLeavePageModal: false,
        showCitiesPlaceholder: false
    },
    validate: function () {
        var _a;
        return ((_a = ConfigSchema.safeParse(this).error) === null || _a === void 0 ? void 0 : _a.errors) || [];
    }
};
export const applyConfig = (config) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if ((_a = config === null || config === void 0 ? void 0 : config.global) === null || _a === void 0 ? void 0 : _a.locale) {
        changeLocale(config.global.locale);
    }
    if ((_b = config === null || config === void 0 ? void 0 : config.global) === null || _b === void 0 ? void 0 : _b.i18n) {
        extendI18n(config.global.i18n);
    }
    if ((_c = config === null || config === void 0 ? void 0 : config.global) === null || _c === void 0 ? void 0 : _c.apiUrl) {
        setApiUrl(config.global.apiUrl);
    }
    const mergedConfig = merge({}, defaultConfig, config);
    mergedConfig.global.additionalResources.extraScripts = [
        ...new Set([
            ...(((_d = defaultConfig.global.additionalResources) === null || _d === void 0 ? void 0 : _d.extraScripts) || []),
            ...(((_e = config.global.additionalResources) === null || _e === void 0 ? void 0 : _e.extraScripts) || [])
        ])
    ];
    mergedConfig.global.additionalResources.extraSvgs = [
        ...new Set([
            ...(((_f = defaultConfig.global.additionalResources) === null || _f === void 0 ? void 0 : _f.extraSvgs) || []),
            ...(((_g = config.global.additionalResources) === null || _g === void 0 ? void 0 : _g.extraSvgs) || [])
        ])
    ];
    if (mergedConfig.global.companyInfo.iataCode !== 'ZZ') {
        const validationErrors = mergedConfig.validate();
        if (validationErrors.length > 0) {
            const fields = validationErrors.map(error => error.path.join('.'));
            const message = `Invalid config, fields: ${fields.join(', ')}`;
            console.error(message, validationErrors);
            captureErrorSentry(message);
        }
    }
    window.__webskyEngineConfig = Object.freeze(Object.assign({}, mergedConfig));
    window['__webskyChangeLocale'] = (locale) => {
        changeLocale(locale).then(() => window.location.reload());
    };
    window['__webskyCoreVersion'] = process.env.PACKAGE_VERSION;
    return mergedConfig;
};
export const ConfigContext = React.createContext(defaultConfig);
export const useConfig = () => useContext(ConfigContext);
export const ModeContext = React.createContext(Mode.Booking);
